/**
 * Lightweight, unopinionated conversion tracking.
 *
 * Every button/link that represents a distinct conversion should call:
 *   track("event_name", { source: "landing_hero", ... })
 *
 * The helper forwards to:
 *   1. window.dataLayer (GTM / GA4 dataLayer, if configured)
 *   2. window.gtag (if a gtag script is loaded)
 *   3. POST /api/events on the app's own backend (fire-and-forget)
 *
 * All three are best-effort; no failure ever throws to the caller.
 */
const API = process.env.REACT_APP_BACKEND_URL;

export function track(event, meta = {}) {
  if (!event) return;
  const payload = { event, meta };

  // 1. GTM/GA4 dataLayer push
  try {
    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event, ...meta });
      if (typeof window.gtag === "function") {
        window.gtag("event", event, meta);
      }
    }
  } catch (e) {
    console.debug("analytics: dataLayer/gtag push failed (non-critical)", e);
  }

  // 2. Backend event log
  try {
    if (API && typeof fetch !== "undefined") {
      fetch(`${API}/api/events`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch((e) => console.debug("analytics: backend event log failed (non-critical)", e));
    }
  } catch (e) {
    console.debug("analytics: backend event log failed (non-critical)", e);
  }
}

/**
 * Convenience wrapper for anchor/link clicks so the caller doesn't have to
 * remember both onClick and href. Usage:
 *   <Link onClick={onLinkClick("hero_start_click", { path: "/intake" })}>
 */
export const onLinkClick = (event, meta) => () => track(event, meta);
